import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Paid } from '@mui/icons-material';
import api from '../../services/api';
import type { DebtInstallment, DebtPlan, DebtSummary, GenerateInstallments, SaveDebtPlan } from '../../types/factory';
import { formatDay } from '../../components/commercial/contact';
import { Kpi, KpiStrip, PageHeader, errorMessage, fmtMoney, isoDay, num, todayIso } from './shared';

const KINDS = ['Banco', 'ARCA', 'Municipio', 'FONDEP', 'Sindicato', 'Seguro', 'Servicio', 'Alquiler', 'Otro'];

const monthLabel = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('es-AR', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** Deudas en cuotas: préstamos, planes de ARCA y municipales, FONDEP, alquileres, servicios. */
const FactoryDebtsPage: React.FC = () => {
  const [summary, setSummary] = useState<DebtSummary | null>(null);
  const [plans, setPlans] = useState<DebtPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.getDebtSummary(12), api.getDebtPlans()]);
      setSummary(s);
      setPlans(p);
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron cargar las deudas'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pay = async (installmentId: number, label: string, amount: number) => {
    if (!window.confirm(`¿Registrar el pago de ${label} por ${fmtMoney(amount)}?`)) return;
    try {
      await api.payDebtInstallment(installmentId);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar el pago'));
    }
  };

  if (!summary || !plans) {
    return error ? <Alert severity="error">{error}</Alert> : <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const maxMonth = Math.max(1, ...summary.months.map(m => m.amount));

  return (
    <Box>
      <PageHeader
        title="Deudas y planes de pago"
        description={<>Préstamos, planes de ARCA y municipales, FONDEP, alquileres y servicios en cuotas.</>}
        actions={<>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreating(true)}>Nueva deuda</Button>
        </>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <KpiStrip>
        <Kpi label="Total adeudado" value={fmtMoney(summary.totalPending)} />
        <Kpi label="Vencido" value={fmtMoney(summary.overdueAmount)} hint={`${summary.overdueCount} cuotas`} color={summary.overdueAmount > 0 ? '#d32f2f' : undefined} />
        <Kpi label="Vence en 30 días" value={fmtMoney(summary.upcomingAmount)} />
      </KpiStrip>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>Cuánto sale por mes</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>El mes actual incluye lo vencido.</Typography>
          {summary.months.map(m => (
            <Tooltip key={m.month} placement="left" title={Object.entries(m.byKind).map(([k, v]) => `${k}: ${fmtMoney(v)}`).join(' · ') || 'Sin cuotas'}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ width: 56 }}>{monthLabel(m.month)}</Typography>
                <Box sx={{ flex: 1, height: 12, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ width: `${(m.amount / maxMonth) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 1 }} />
                </Box>
                <Typography variant="caption" sx={{ width: 96, textAlign: 'right' }}>{fmtMoney(m.amount)}</Typography>
              </Stack>
            </Tooltip>
          ))}
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <Typography fontWeight={700} sx={{ p: 2, pb: 0 }}>Vencidas y próximos 30 días</Typography>
          <Table size="small">
            <TableBody>
              {summary.upcoming.length === 0 && <TableRow><TableCell sx={{ color: 'text.secondary' }}>Nada por pagar en los próximos 30 días.</TableCell></TableRow>}
              {summary.upcoming.map(u => (
                <TableRow key={u.installmentId}>
                  <TableCell sx={{ color: u.overdue ? 'error.main' : undefined }}>{formatDay(u.dueDate)}</TableCell>
                  <TableCell>{u.planName} <Typography component="span" variant="caption" color="text.secondary">· cuota {u.number}</Typography></TableCell>
                  <TableCell align="right">{fmtMoney(u.amount)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Pagar" onClick={() => pay(u.installmentId, `${u.planName} cuota ${u.number}`, u.amount)}><Paid fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Stack>

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Deuda</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Acreedor / N°</TableCell>
                <TableCell align="right">Cuotas</TableCell>
                <TableCell align="right">Pendiente</TableCell>
                <TableCell align="right">Vencido</TableCell>
                <TableCell>Próxima cuota</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin deudas cargadas.</TableCell></TableRow>}
              {plans.map(p => (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(p.id)}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.kind ? <Chip size="small" label={p.kind} /> : ''}</TableCell>
                  <TableCell>{[p.creditor, p.reference].filter(Boolean).join(' · ')}</TableCell>
                  <TableCell align="right">{p.paidCount}/{p.installmentsCount}</TableCell>
                  <TableCell align="right"><b>{fmtMoney(p.pending)}</b></TableCell>
                  <TableCell align="right" sx={{ color: p.overdueAmount > 0 ? 'error.main' : undefined }}>
                    {p.overdueAmount > 0 ? `${fmtMoney(p.overdueAmount)} (${p.overdueCount})` : '—'}
                  </TableCell>
                  <TableCell>{p.nextDueDate ? `${formatDay(p.nextDueDate)} · ${fmtMoney(p.nextAmount)}` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {creating && <PlanDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {detailId && <PlanDetail id={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </Box>
  );
};

// ==================== Alta / edición ====================

type GenForm = { count: string; amount: string; firstDueDate: string; paidCount: string };
const emptyGen = (): GenForm => ({ count: '', amount: '', firstDueDate: todayIso(), paidCount: '0' });
const toGenerate = (g: GenForm): GenerateInstallments => ({
  count: Math.round(num(g.count)), amount: num(g.amount), firstDueDate: g.firstDueDate, paidCount: Math.round(num(g.paidCount)),
});

const GeneratorFields: React.FC<{ g: GenForm; onChange: (g: GenForm) => void }> = ({ g, onChange }) => (
  <Stack spacing={2}>
    <Stack direction="row" spacing={2}>
      <TextField size="small" type="number" label="Cantidad de cuotas" value={g.count} onChange={e => onChange({ ...g, count: e.target.value })} />
      <TextField size="small" type="number" label="Monto de la cuota $" value={g.amount} onChange={e => onChange({ ...g, amount: e.target.value })} />
    </Stack>
    <Stack direction="row" spacing={2}>
      <TextField size="small" type="date" label="Vence la primera" value={g.firstDueDate} onChange={e => onChange({ ...g, firstDueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
      <TextField size="small" type="number" label="Ya pagas" value={g.paidCount} onChange={e => onChange({ ...g, paidCount: e.target.value })} helperText="Si el plan ya empezó" />
    </Stack>
    {num(g.count) > 0 && num(g.amount) > 0 && (
      <Typography variant="caption" color="text.secondary">
        Total {fmtMoney(num(g.count) * num(g.amount))} · queda {fmtMoney((num(g.count) - num(g.paidCount)) * num(g.amount))}
      </Typography>
    )}
  </Stack>
);

const PlanDialog: React.FC<{ plan?: DebtPlan; onClose: () => void; onSaved: () => void }> = ({ plan, onClose, onSaved }) => {
  const [f, setF] = useState<SaveDebtPlan>({
    name: plan?.name || '', kind: plan?.kind || 'Banco', creditor: plan?.creditor || '', reference: plan?.reference || '',
    isActive: plan?.isActive ?? true, notes: plan?.notes || '',
  });
  const [g, setG] = useState<GenForm>(emptyGen());
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      if (plan) await api.updateDebtPlan(plan.id, f);
      else await api.createDebtPlan({ ...f, generate: num(g.count) > 0 ? toGenerate(g) : null });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la deuda'));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{plan ? 'Editar deuda' : 'Nueva deuda'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField size="small" label="Nombre" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required sx={{ flex: 1 }} placeholder="Préstamo Banco Provincia" autoFocus />
            <TextField size="small" select label="Tipo" value={f.kind || ''} onChange={e => setF({ ...f, kind: e.target.value })} sx={{ width: 150 }}>
              {KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField size="small" label="Acreedor" value={f.creditor || ''} onChange={e => setF({ ...f, creditor: e.target.value })} sx={{ flex: 1 }} />
            <TextField size="small" label="N° préstamo / convenio" value={f.reference || ''} onChange={e => setF({ ...f, reference: e.target.value })} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Notas" value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} multiline />
          {!plan && (
            <>
              <Divider />
              <Typography variant="subtitle2">Cuotas mensuales (opcional, se pueden agregar después)</Typography>
              <GeneratorFields g={g} onChange={setG} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!f.name}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Detalle de cuotas ====================

const PlanDetail: React.FC<{ id: number; onClose: () => void }> = ({ id, onClose }) => {
  const [plan, setPlan] = useState<DebtPlan | null>(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [editing, setEditing] = useState<(Omit<DebtInstallment, 'amount' | 'paidAmount'> & { amount: string; paidAmount: string }) | null>(null);
  const [gen, setGen] = useState<GenForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { api.getDebtPlan(id).then(setPlan).catch(e => setError(errorMessage(e, 'No se pudo abrir la deuda'))); }, [id]);
  useEffect(() => { load(); }, [load]);

  const today = todayIso();

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try {
      await fn();
      setError(null);
      load();
    } catch (e) {
      setError(errorMessage(e, fallback));
    }
  };

  const partial = (i: DebtInstallment) => {
    const left = i.amount - i.paidAmount;
    const v = window.prompt(`¿Cuánto se pagó de la cuota ${i.number}? (quedan ${fmtMoney(left)})`, String(Math.round(left)));
    if (v == null) return;
    run(() => api.payDebtInstallment(i.id, { amount: num(v) }), 'No se pudo registrar el pago');
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center">
          <Box sx={{ flex: 1 }}>{plan?.name || 'Deuda'}</Box>
          {plan && <IconButton size="small" onClick={() => setEditingPlan(true)}><Edit fontSize="small" /></IconButton>}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {!plan ? <CircularProgress /> : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              {plan.kind && <Chip size="small" label={plan.kind} />}
              <Typography variant="body2">{[plan.creditor, plan.reference].filter(Boolean).join(' · ')}</Typography>
              <Typography variant="body2">Total {fmtMoney(plan.total)}</Typography>
              <Typography variant="body2">Pagado {fmtMoney(plan.paid)}</Typography>
              <Typography variant="body2"><b>Pendiente {fmtMoney(plan.pending)}</b></Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Cuota</TableCell>
                    <TableCell>Vence</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell>Pagada el</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.installments.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>Sin cuotas: agregalas abajo.</TableCell></TableRow>}
                  {plan.installments.map(i => {
                    const paid = i.paidAmount >= i.amount;
                    const overdue = !paid && isoDay(i.dueDate) < today;
                    return (
                      <TableRow key={i.id} sx={{ opacity: paid ? 0.55 : 1 }}>
                        <TableCell>{i.number}</TableCell>
                        <TableCell sx={{ color: overdue ? 'error.main' : undefined }}>{formatDay(i.dueDate)}</TableCell>
                        <TableCell align="right">{fmtMoney(i.amount)}</TableCell>
                        <TableCell align="right">{i.paidAmount > 0 ? fmtMoney(i.paidAmount) : '—'}</TableCell>
                        <TableCell>{i.paidAt ? formatDay(i.paidAt) : overdue ? <Chip size="small" color="error" label="vencida" /> : ''}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {!paid && <Button size="small" onClick={() => run(() => api.payDebtInstallment(i.id), 'No se pudo registrar el pago')}>Pagar</Button>}
                          {!paid && <Button size="small" onClick={() => partial(i)}>Parcial</Button>}
                          <IconButton size="small" onClick={() => setEditing({ ...i, amount: String(i.amount), paidAmount: String(i.paidAmount) })}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => window.confirm(`¿Borrar la cuota ${i.number}?`) && run(() => api.deleteDebtInstallment(i.id), 'No se pudo borrar')}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {gen ? (
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Agregar cuotas</Typography>
                <GeneratorFields g={gen} onChange={setGen} />
                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
                  <Button onClick={() => setGen(null)}>Cancelar</Button>
                  <Button variant="contained" disabled={num(gen.count) <= 0 || num(gen.amount) <= 0}
                    onClick={() => run(async () => { await api.addDebtInstallments(plan.id, toGenerate(gen)); setGen(null); }, 'No se pudieron agregar las cuotas')}>Agregar</Button>
                </Stack>
              </Card>
            ) : (
              <Button startIcon={<Add />} sx={{ alignSelf: 'flex-start' }} onClick={() => setGen({ ...emptyGen(), count: '1' })}>Agregar cuotas</Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {plan && (
          <Button color="error" sx={{ mr: 'auto' }} onClick={async () => {
            if (!window.confirm(`¿Borrar ${plan.name} con todas sus cuotas?`)) return;
            await api.deleteDebtPlan(plan.id);
            onClose();
          }}>Borrar deuda</Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {editingPlan && plan && <PlanDialog plan={plan} onClose={() => setEditingPlan(false)} onSaved={() => { setEditingPlan(false); load(); }} />}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar cuota {editing?.number}</DialogTitle>
        {editing && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField size="small" type="number" label="N° de cuota" value={editing.number} onChange={e => setEditing({ ...editing, number: Math.round(num(e.target.value)) })} />
              <TextField size="small" type="date" label="Vence" value={isoDay(editing.dueDate)} onChange={e => setEditing({ ...editing, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="number" label="Monto $" value={editing.amount} onChange={e => setEditing({ ...editing, amount: e.target.value })} />
              <TextField size="small" type="number" label="Pagado $" value={editing.paidAmount} onChange={e => setEditing({ ...editing, paidAmount: e.target.value })} />
              <TextField size="small" type="date" label="Pagada el" value={isoDay(editing.paidAt)} onChange={e => setEditing({ ...editing, paidAt: e.target.value || null })} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="Notas" value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => editing && run(async () => {
            const { id: installmentId, amount, paidAmount, ...rest } = editing;
            await api.updateDebtInstallment(installmentId, { ...rest, dueDate: isoDay(rest.dueDate), amount: num(amount), paidAmount: num(paidAmount) });
            setEditing(null);
          }, 'No se pudo guardar la cuota')}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FactoryDebtsPage;
